import React from 'react';
import { motion } from 'framer-motion';
import { Mail, FileText, Calendar, Loader2 } from 'lucide-react';

/**
 * Resource Card Component
 * Displays a downloadable resource with metadata
 * Features gradient header with title text overlay
 */

// Gradient styles for each category
const categoryGradients = {
  'Guides': 'from-[#5B2C91] via-[#8B3A8F] to-[#D8456F]', // Purple to Pink
  'Templates': 'from-[#B8315A] via-[#D8456F] to-[#F54952]', // Pink to Red
  'Checklists': 'from-[#3B5998] via-[#2C7A9B] to-[#1BA098]', // Blue to Teal
  'Presentations': 'from-[#422680] via-[#660F56] to-[#8B3A8F]' // Default purple gradient
};

// Subtitle text for categories
const categorySubtitles = {
  'cybersecurity-checklist-2025': 'Essential Eight Controls\nAustralian Business Guide',
  'incident-response-template': 'Privacy Act Compliant\nReady-to-Use',
  'iso27001-gap-analysis': '2022 Certification\nComplete Checklist'
};

export function ResourceCard({ resource, onDownload, isDownloading }) {
  const gradientClass = categoryGradients[resource.category] || categoryGradients['Presentations'];
  const subtitle = categorySubtitles[resource.id] || '';
  
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: 20 }}
      transition={{ duration: 0.3 }}
      className="bg-white rounded-2xl overflow-hidden border border-gray-100 hover:border-[#422680] hover:shadow-xl transition-all group flex flex-col h-full"
    >
      {/* Gradient Header with Title Overlay */}
      <div className={`relative h-48 overflow-hidden bg-gradient-to-br ${gradientClass} flex flex-col justify-center items-center text-center p-6`}>
        {/* Category Badge */}
        <div className="absolute top-4 right-4 bg-[#F54952] text-white text-xs font-bold px-3 py-1 rounded-full">
          {resource.category}
        </div>
        
        {/* Title Text on Gradient */}
        <h2 className="text-white font-bold text-2xl mb-3 leading-tight">
          {resource.title}
        </h2>
        
        {/* Subtitle Text */}
        {subtitle && (
          <div className="text-white/90 text-sm space-y-1">
            {subtitle.split('\n').map((line, idx) => (
              <div key={idx}>{line}</div>
            ))}
          </div>
        )}
      </div>

      {/* Content */}
      <div className="p-6 flex flex-col flex-1">
        <h3 className="text-xl font-bold text-gray-900 mb-2">{resource.title}</h3>
        <p className="text-gray-600 text-sm mb-4 flex-1">{resource.description}</p>

        {/* Metadata */}
        <div className="space-y-3 mb-6 pt-4 border-t border-gray-50">
          <div className="flex items-center gap-2 text-gray-500 text-sm">
            <FileText size={16} className="text-[#422680]" />
            <span>{resource.fileSize}</span>
          </div>
          <div className="flex items-center gap-2 text-gray-500 text-sm">
            <Calendar size={16} className="text-[#422680]" />
            <span>Added {new Date(resource.dateAdded).toLocaleDateString('en-AU', {
              year: 'numeric',
              month: 'short',
              day: 'numeric'
            })}</span>
          </div>
        </div>

        {/* Download Button (gradient style from TrainingPage) */}
        <button
          onClick={() => onDownload(resource.id)}
          disabled={isDownloading}
          className="w-full bg-gradient-to-r from-[#422680] via-[#660F56] to-[#F54952] text-white py-3 rounded-lg font-semibold hover:shadow-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
        >
          {isDownloading ? (
            <>
              <Loader2 size={18} className="animate-spin" />
              Sending link...
            </>
          ) : (
            <>
              <Mail size={18} />
              Email me the link
            </>
          )}
        </button>
      </div>
    </motion.div>
  );
}
